import type { ImportedEquipmentDraft, ImportIssue } from "./equipment-import.js";

/**
 * STAGING DA IMPORTAÇÃO (Bloco 04, complemento).
 *
 * Camada intermediária obrigatória: o arquivo é lido para cá, criticado aqui,
 * corrigido aqui e só depois vira Equipment. Enquanto um registro está no
 * staging, NENHUM Equipment oficial existe.
 *
 * O staging é a mesma estrutura para XLSX e PDF. O adaptador de entrada muda;
 * daqui para frente a regra é uma só.
 */

/** Estado de extração de um campo — relevante sobretudo em PDF/OCR. */
export type FieldStatus =
  | "CONFIRMADO"
  | "REVISÃO NECESSÁRIA"
  | "BAIXA CONFIANÇA"
  | "NÃO RECONHECIDO"
  | "CORRIGIDO PELO USUÁRIO";

/** Situação do registro frente ao que já existe no cadastro. */
export type StagingDisposition =
  | "NOVO"
  | "EXISTENTE / SEM ALTERAÇÃO"
  | "COMPLEMENTAÇÃO"
  | "CONFLITO"
  | "BLOQUEADO";

export interface FieldExtraction {
  readonly field: string;
  readonly status: FieldStatus;
  /** 0..1. Extração direta de texto/planilha vale 1. */
  readonly confidence: number;
  readonly extractedValue: string | null;
  readonly correctedValue: string | null;
  /** Página do PDF de origem, quando aplicável. */
  readonly page: number | null;
  readonly region: string | null;
  readonly correctedBy: string | null;
  readonly correctedAt: string | null;
}

/** Diferença entre o valor já cadastrado e o valor do arquivo. */
export interface FieldConflict {
  readonly field: string;
  readonly label: string;
  readonly currentValue: string;
  readonly importedValue: string;
}

export interface StagingRecord {
  readonly id: string;
  readonly rowNumber: number;
  readonly kind: "PIPING_SYSTEM" | "PIPING_LINE";
  readonly tag: string;
  readonly systemTag: string | null;
  readonly draft: ImportedEquipmentDraft;
  readonly disposition: StagingDisposition;
  /** Id do Equipment já existente, quando o registro não é novo. */
  readonly existingEquipmentId: string | null;
  readonly conflicts: readonly FieldConflict[];
  readonly extractions: readonly FieldExtraction[];
  readonly issues: readonly ImportIssue[];
  /** O usuário pode tirar um registro do lote antes de confirmar. */
  readonly selected: boolean;
}

/** Campos sem os quais o cadastro não faz sentido — baixa confiança neles bloqueia. */
export const ESSENTIAL_FIELDS: readonly string[] = ["tag", "systemTag"];

export const LOW_CONFIDENCE_THRESHOLD = 0.8;

export function isBlockingExtraction(extraction: FieldExtraction): boolean {
  if (!ESSENTIAL_FIELDS.includes(extraction.field)) return false;
  if (extraction.status === "CORRIGIDO PELO USUÁRIO" || extraction.status === "CONFIRMADO") return false;
  return extraction.status === "BAIXA CONFIANÇA" || extraction.status === "NÃO RECONHECIDO" || extraction.confidence < LOW_CONFIDENCE_THRESHOLD;
}

/** Extração de origem estruturada (XLSX): confiança total, sem página. */
export function directExtraction(field: string, value: string | null): FieldExtraction {
  return {
    field, status: "CONFIRMADO", confidence: 1, extractedValue: value, correctedValue: null,
    page: null, region: null, correctedBy: null, correctedAt: null
  };
}

/** Aplica a correção manual do usuário sem tocar no arquivo de origem. */
export function applyCorrection(
  extraction: FieldExtraction,
  correctedValue: string,
  userId: string,
  now: string
): FieldExtraction {
  return {
    ...extraction,
    status: "CORRIGIDO PELO USUÁRIO",
    correctedValue,
    correctedBy: userId,
    correctedAt: now
  };
}

/** Valor que vale para o cadastro: o corrigido pelo usuário tem precedência. */
export function effectiveValue(extraction: FieldExtraction): string | null {
  return extraction.correctedValue ?? extraction.extractedValue;
}

// --------------------------------------------------------- comparação com o cadastro

/**
 * Campos comparados para decidir entre SEM ALTERAÇÃO, COMPLEMENTAÇÃO e
 * CONFLITO.
 *
 * `equipmentField` só aparece quando o nome no cadastro difere do nome no
 * rascunho. Comparar `draft.minimumThickness` com `existing.minimumThickness`
 * — que não existe em `Equipment`, onde o campo é `minimumRequiredThickness` —
 * daria "cadastro vazio" sempre, e proporia complementar o que já estava lá.
 */
export const COMPARABLE_FIELDS: ReadonlyArray<{ field: string; label: string; equipmentField?: string }> = [
  { field: "service", label: "Sistema / serviço" },
  { field: "fluid", label: "Fluido" },
  { field: "fluidCode", label: "Código do fluido" },
  { field: "fluidClass", label: "Classe de fluido" },
  { field: "nr13Category", label: "Categoria NR-13" },
  { field: "designPressure", label: "Pressão de projeto" },
  { field: "operatingPressure", label: "Pressão de operação" },
  { field: "designTemperature", label: "Temperatura de projeto" },
  { field: "operatingTemperature", label: "Temperatura de operação" },
  { field: "pmtaValue", label: "PMTA" },
  { field: "pressureClass", label: "Classe de pressão" },
  { field: "spec", label: "Spec" },
  { field: "material", label: "Material base" },
  { field: "nps", label: "Diâmetro nominal" },
  { field: "schedule", label: "SCH" },
  { field: "nominalThickness", label: "Espessura nominal" },
  /**
   * Campos que a Lista de Linhas real traz e que viraram campo de primeira
   * classe do rascunho (CLAUDE.md §42.1). Sem eles aqui, a comparação não
   * enxergava que o arquivo preencheria um cadastro vazio — e o
   * COMPLEMENTAÇÃO nunca era proposto para justamente os dados que a
   * correção do mapeamento passou a capturar.
   */
  { field: "minimumThickness", label: "Espessura mínima", equipmentField: "minimumRequiredThickness" },
  { field: "outsideDiameter", label: "Diâmetro externo" },
  { field: "corrosionAllowance", label: "Sobre-espessura de corrosão" },
  { field: "materialFamily", label: "Família de material" },
  { field: "fabricationStandard", label: "Norma de fabricação" },
  { field: "dimensionalStandard", label: "Norma dimensional" },
  { field: "lineNumber", label: "Nº linha" },
  { field: "fromReference", label: "DE" },
  { field: "toReference", label: "PARA" },
  { field: "drawingReference", label: "Desenho" },
  { field: "flowDiagramReference", label: "Fluxograma" }
];

function asComparable(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object" && value !== null && "value" in (value as Record<string, unknown>)) {
    const measurement = value as { value: number | null; unit?: string };
    return measurement.value === null ? null : String(measurement.value);
  }
  return String(value);
}

export interface ComparisonOutcome {
  readonly disposition: Extract<StagingDisposition, "EXISTENTE / SEM ALTERAÇÃO" | "COMPLEMENTAÇÃO" | "CONFLITO">;
  readonly conflicts: readonly FieldConflict[];
  /** Campos vazios no cadastro que o arquivo preencheria. */
  readonly complements: readonly string[];
}

/**
 * Compara o registro do arquivo com o Equipment já cadastrado.
 *
 * - Valor igual em tudo → EXISTENTE / SEM ALTERAÇÃO.
 * - Arquivo preenche campo que estava vazio → COMPLEMENTAÇÃO.
 * - Arquivo diverge de valor já preenchido → CONFLITO. Nunca sobrescreve
 *   por conta própria: a decisão é do usuário.
 */
export function compareWithExisting(
  draft: ImportedEquipmentDraft,
  existing: Record<string, unknown>
): ComparisonOutcome {
  const conflicts: FieldConflict[] = [];
  const complements: string[] = [];

  for (const { field, label, equipmentField } of COMPARABLE_FIELDS) {
    const importedRaw = (draft as unknown as Record<string, unknown>)[field];
    const imported = asComparable(importedRaw);
    if (imported === null) continue;

    const current = asComparable(existing[equipmentField ?? field]);
    if (current === null) { complements.push(label); continue; }
    if (current !== imported) {
      conflicts.push({ field, label, currentValue: current, importedValue: imported });
    }
  }

  if (conflicts.length > 0) return { disposition: "CONFLITO", conflicts, complements };
  if (complements.length > 0) return { disposition: "COMPLEMENTAÇÃO", conflicts: [], complements };
  return { disposition: "EXISTENTE / SEM ALTERAÇÃO", conflicts: [], complements: [] };
}

/**
 * BACKFILL — o que o arquivo preencheria num cadastro já existente.
 *
 * Devolve só os campos que estão VAZIOS no cadastro e que o arquivo traz.
 * Campo já preenchido nunca entra, nem quando o arquivo discorda: divergência
 * é `CONFLITO` e decisão do usuário, não coisa que a importação resolva
 * sozinha. Por isso o patch é sempre seguro de aplicar — ele só acrescenta.
 *
 * É esta função que torna a reimportação da mesma planilha um backfill: as
 * Tubulações importadas antes da correção do mapeamento (CLAUDE.md §42)
 * ficaram sem MATERIAL, DN, CAT. NR-13 e afins, e a planilha de origem não é
 * guardada em lugar nenhum — reimportá-la é o único caminho possível, e ele
 * precisa completar em vez de apenas relatar "já existe".
 */
export function complementPatch(
  draft: ImportedEquipmentDraft,
  existing: Record<string, unknown>
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const { field, equipmentField } of COMPARABLE_FIELDS) {
    const importedRaw = (draft as unknown as Record<string, unknown>)[field];
    if (asComparable(importedRaw) === null) continue;
    const target = equipmentField ?? field;
    if (asComparable(existing[target]) !== null) continue; // já preenchido: não se toca
    patch[target] = importedRaw;
  }
  return patch;
}

// ----------------------------------------------------------- validações técnicas

export interface TechnicalDivergence {
  readonly rule: string;
  readonly message: string;
}

/**
 * Coerências técnicas exigidas pelo comando. Nada é corrigido
 * automaticamente: o valor recebido é preservado e a divergência é exibida
 * para o usuário decidir.
 *
 * Compara sempre valores já normalizados para a unidade canônica; comparar
 * bar com kgf/cm² produziria divergência falsa.
 */
export function validateTechnicalCoherence(draft: ImportedEquipmentDraft): TechnicalDivergence[] {
  const out: TechnicalDivergence[] = [];
  const designPressure = draft.designPressure?.value ?? null;
  const operatingPressure = draft.operatingPressure?.value ?? null;
  const designTemperature = draft.designTemperature?.value ?? null;
  const operatingTemperature = draft.operatingTemperature?.value ?? null;
  const pmta = draft.pmtaValue;
  const nominal = draft.nominalThickness?.value ?? null;
  const minimum = draft.minimumThickness?.value ?? null;

  if (designPressure !== null && operatingPressure !== null && operatingPressure > designPressure) {
    out.push({
      rule: "P_OPERACAO_MAIOR_QUE_PROJETO",
      message: `Pressão de operação (${operatingPressure}) maior que a de projeto (${designPressure}).`
    });
  }
  if (designPressure !== null && pmta !== null && pmta < designPressure) {
    out.push({
      rule: "PMTA_MENOR_QUE_PROJETO",
      message: `PMTA (${pmta}) menor que a pressão de projeto (${designPressure}).`
    });
  }
  if (designTemperature !== null && operatingTemperature !== null && operatingTemperature > designTemperature) {
    out.push({
      rule: "T_OPERACAO_MAIOR_QUE_PROJETO",
      message: `Temperatura de operação (${operatingTemperature}) maior que a de projeto (${designTemperature}).`
    });
  }
  if (nominal !== null && minimum !== null && minimum > nominal) {
    out.push({
      rule: "ESPESSURA_MINIMA_MAIOR_QUE_NOMINAL",
      message: `Espessura mínima (${minimum}) maior que a nominal (${nominal}).`
    });
  }
  return out;
}

/** Só entra no lote o registro selecionado que não esteja bloqueado nem em conflito aberto. */
export function isConfirmable(record: StagingRecord): boolean {
  if (!record.selected) return false;
  if (record.disposition === "BLOQUEADO" || record.disposition === "CONFLITO") return false;
  if (record.issues.some(issue => issue.severity === "ERROR")) return false;
  return !record.extractions.some(isBlockingExtraction);
}

/** Registros que serão efetivamente gravados: novos e complementações selecionadas. */
export function recordsToPersist(records: readonly StagingRecord[]): StagingRecord[] {
  return records.filter(record => isConfirmable(record) && record.disposition === "NOVO");
}

/** Registros reaproveitados: já existem e não serão recriados (idempotência). */
export function recordsReused(records: readonly StagingRecord[]): StagingRecord[] {
  return records.filter(r => r.disposition === "EXISTENTE / SEM ALTERAÇÃO" || r.disposition === "COMPLEMENTAÇÃO");
}
